import { useState, useEffect, useCallback } from "react";
import { apiFetch, ApiError } from "@/lib/api-client";
import type {
  BulkImportBatchDetail,
  BulkImportCommitOptions,
  BulkImportCommitResult,
  BulkImportRecord,
} from "../types";

interface UseBulkImportBatchResult<
  TNormalized extends BulkImportRecord = BulkImportRecord,
> {
  batch: BulkImportBatchDetail<TNormalized> | null;
  commitResult: BulkImportCommitResult | null;
  isLoading: boolean;
  isCommitting: boolean;
  error: Error | null;
  commitError: Error | null;
  refetch: () => void;
  commit: (
    options?: BulkImportCommitOptions,
  ) => Promise<BulkImportCommitResult>;
  resetCommitResult: () => void;
}

export function useBulkImportBatch<
  TNormalized extends BulkImportRecord = BulkImportRecord,
>(batchId: string | null | undefined): UseBulkImportBatchResult<TNormalized> {
  const [batch, setBatch] = useState<BulkImportBatchDetail<TNormalized> | null>(
    null,
  );
  const [commitResult, setCommitResult] =
    useState<BulkImportCommitResult | null>(null);
  const [isLoading, setIsLoading] = useState(Boolean(batchId));
  const [isCommitting, setIsCommitting] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [commitError, setCommitError] = useState<Error | null>(null);
  const [fetchKey, setFetchKey] = useState(0);

  useEffect(() => {
    if (!batchId) {
      setBatch(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    const fetchBatch = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const data = await apiFetch.get<BulkImportBatchDetail<TNormalized>>(
          `/api/bulk-imports/${batchId}`,
        );
        setBatch(data);
      } catch (err) {
        setError(toError(err));
        setBatch(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBatch();
  }, [batchId, fetchKey]);

  const refetch = useCallback(() => setFetchKey((key) => key + 1), []);

  const commit = useCallback(
    async (
      options: BulkImportCommitOptions = {},
    ): Promise<BulkImportCommitResult> => {
      if (!batchId) {
        throw new Error("Bulk import batch id is required.");
      }

      setIsCommitting(true);
      setCommitError(null);
      setCommitResult(null);

      try {
        const data = await apiFetch.post<BulkImportCommitResult>(
          `/api/bulk-imports/${batchId}/commit`,
          options,
        );
        setCommitResult(data);
        setBatch((current) =>
          current ? { ...current, batch: data.batch } : current,
        );
        return data;
      } catch (err) {
        const nextError = toError(err);
        setCommitError(nextError);
        throw err;
      } finally {
        setIsCommitting(false);
      }
    },
    [batchId],
  );

  const resetCommitResult = useCallback(() => {
    setCommitResult(null);
    setCommitError(null);
  }, []);

  return {
    batch,
    commitResult,
    isLoading,
    isCommitting,
    error,
    commitError,
    refetch,
    commit,
    resetCommitResult,
  };
}

function toError(error: unknown): Error {
  if (error instanceof ApiError) {
    return error;
  }

  return error instanceof Error ? error : new Error("Unknown error");
}
